import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGetBlogDetailsQuery } from '../../features/blogApiSlice';
import { Helmet } from 'react-helmet';
import { OurIngredients, TakeTheTestButton } from '../../Constants';
import { useTranslation } from 'react-i18next';
import { selectSlug } from '../../features/contentsSlice';
import { useSelector } from 'react-redux';
import { selectCurrentLanguage } from '../../features/langSlice';
import CustomLink from '../CustomLink';

const IngredientDetails = () => {
  const { slug } = useParams();
  const slugg = useSelector(selectSlug);

  const [data, setData] = useState();
  const selectedLang = useSelector(selectCurrentLanguage);
  const [lang, setLang] = useState();
  const { t } = useTranslation();

  const { data: fetchDetails, isSuccess, refetch } = useGetBlogDetailsQuery({ slug: slug, lang: lang });

  useEffect(() => {
    setLang(selectedLang === 'us' ? 'en' : selectedLang);

    refetch();
  }, [refetch, lang, selectedLang]);

  useEffect(() => {
    if (isSuccess) {
      setData(fetchDetails.data[0]);
    }
  }, [fetchDetails, isSuccess]);

  return (
    <>
      <Helmet>
        <title>{`Riise - ${data?.title}`}</title>
        <meta name="title" content={data?.meta_title} />
        <meta name="description" content={data?.meta_description} />
      </Helmet>

      {/* Hero */}
      <section style={{ backgroundColor: '#103642' }}>
        <div className="container t-py-10">
          <nav className="t-mb-8" aria-label="Breadcrumb">
            <ol
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                listStyle: 'none',
                padding: 0,
                margin: 0,
                flexWrap: 'wrap',
              }}
            >
              <li>
                <CustomLink
                  to="/"
                  style={{ color: 'rgba(249,249,249,0.5)', textDecoration: 'none', fontSize: '13px' }}
                >
                  Home
                </CustomLink>
              </li>
              <li style={{ color: 'rgba(249,249,249,0.3)', fontSize: '13px' }}>/</li>
              <li>
                <CustomLink
                  to="/ingredients"
                  style={{ color: 'rgba(249,249,249,0.5)', textDecoration: 'none', fontSize: '13px' }}
                >
                  {t(OurIngredients)}
                </CustomLink>
              </li>
              <li style={{ color: 'rgba(249,249,249,0.3)', fontSize: '13px' }}>/</li>
              <li style={{ color: '#F6E147', fontSize: '13px' }}>{data?.title}</li>
            </ol>
          </nav>

          <div className="t-flex t-items-center t-justify-between t-gap-8">
            <div>
              <div
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '1.5px',
                  color: '#5DCAA5',
                  marginBottom: '12px',
                }}
              >
                {t('IngredientsHero_Eyebrow')}
              </div>
              <h1
                style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: 'clamp(32px, 5vw, 48px)',
                  fontWeight: 400,
                  color: '#F9F9F9',
                  lineHeight: 1.15,
                  margin: 0,
                }}
              >
                {data?.title}
              </h1>
            </div>
            {data?.picture && (
              <div className="t-hidden md:t-block t-flex-shrink-0">
                <div
                  style={{
                    width: '180px',
                    height: '180px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '1px solid rgba(249,249,249,0.15)',
                  }}
                >
                  <img
                    src={`${process.env.REACT_APP_PUBLIC_URL}${data.picture}`}
                    alt={data.title || ''}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding" style={{ minHeight: '50vh' }}>
        <div className="container">
          <div className="row">
            <div className="col-xl-8">
              {data?.picture && (
                <div className="md:t-hidden t-mb-6 t-flex t-justify-center">
                  <div style={{ width: '120px', height: '120px', borderRadius: '50%', overflow: 'hidden' }}>
                    <img
                      src={`${process.env.REACT_APP_PUBLIC_URL}${data.picture}`}
                      alt={data.title || ''}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                </div>
              )}
              <div className="blog-detail-inner">
                <span dangerouslySetInnerHTML={{ __html: data?.contents }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: '#103642', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '640px', margin: '0 auto', textAlign: 'center' }}>
            <h2
              style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: 'clamp(28px, 4vw, 42px)',
                fontWeight: 400,
                color: '#F9F9F9',
                lineHeight: 1.2,
                marginBottom: '28px',
              }}
            >
              {t('IngredientCTA_HeadingPre')}{' '}
              <em style={{ color: '#F6E147', fontStyle: 'italic' }}>{data?.title}</em>{' '}
              {t('IngredientCTA_HeadingPost')}
            </h2>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '20px',
                marginBottom: '32px',
              }}
            >
              {['IngredientCTA_Pill1', 'IngredientCTA_Pill2', 'IngredientCTA_Pill3'].map((key) => (
                <span
                  key={key}
                  style={{
                    fontSize: '13px',
                    color: 'rgba(249,249,249,0.65)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span style={{ color: '#5DCAA5', fontWeight: 700 }}>✓</span>
                  {t(key)}
                </span>
              ))}
            </div>
            <CustomLink to={slugg}>
              <span
                style={{
                  display: 'inline-block',
                  backgroundColor: '#F6E147',
                  color: '#103642',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: '15px',
                  padding: '14px 36px',
                  borderRadius: '100px',
                  cursor: 'pointer',
                }}
              >
                {t(TakeTheTestButton)}
              </span>
            </CustomLink>
            <div style={{ marginTop: '12px', color: 'rgba(249,249,249,0.4)', fontSize: '12px' }}>
              {t('IngredientCTA_TimeNote')}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default IngredientDetails;
